import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  FirstDataRenderedEvent,
  ICellRendererParams,
  IRowNode,
} from '@ag-grid-community/core';
import {
  AfterViewInit,
  Component,
  inject,
  input,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FieldTypes } from '@digiwall/ag-grid';
import { LinesEditorComponent } from '../../../../../components';
import { LocalizationService, SubscriptionService } from '@abp/ng.core';
import {
  CreateDeliveryNoteLineDto,
  CustomerDeliveryNoteService,
  DocLineRole,
} from '@digiwall/goliath-documents/proxy';
import { ToasterService } from '@abp/ng.theme.shared';
import { NewGuid } from '@digiwall/core';
import { CurrentCompanyStore, GroupRowCellRendererComponent } from '@digiwall/goliath-core';
import { concatMap, of, tap, Observable, Subject, takeUntil, from } from 'rxjs';
import { ColumnDefinitionsService } from '../../../../../services/column-definitions.service';
import { ArticleRangeValueStore } from '@digiwall/goliath-products';
import { StorageLocationLookupDto } from '@digiwall/goliath-core/proxy';
import {
  ArticleLotDto,
  ArticleRangeValueDto,
  ArticleSerialDto,
} from '@digiwall/goliath-products/proxy';
import { DocType } from '../../../../base-docs/detail/doc-type';

@Component({
  selector: 'document-delivery-note-creation-grid',
  templateUrl: './delivery-note-creation-grid.component.html',
  imports: [LinesEditorComponent],
})
export class DeliveryNoteCreationGridComponent implements AfterViewInit, OnDestroy {
  protected readonly currentCompanyStore = inject(CurrentCompanyStore);
  protected readonly columnDefinitionsService = inject(ColumnDefinitionsService);
  protected readonly articleRangeValueStore = inject(ArticleRangeValueStore);

  @Input() set lines(value: any[]) {
    this._lines = value?.filter(x => !x.isDeposit);
    this.processLines();
  }
  _lines: CreateDeliveryNoteLineDto[];
  public get lines() {
    return this._lines;
  }
  @Input() salesPurchaseId: string;
  allowPartialDelivery = input<boolean>(false);

  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  private destroy$ = new Subject<void>();
  private totalRow = {
    id: NewGuid,
    bottomRow: true,
    description: this.localizationService.instant('Doc::Total'),
    selectedQuantityToDeliver: 0,
    totalWeight: 0,
    totalPrice: 0,
    isBottom: true,
  };

  private isCellEditable = (params: EditableCallbackParams<CreateDeliveryNoteLineDto, any>) =>
    // Editable if article line AND it is NOT a reservation for a stock managed article (if not reservation OR not stock managed, you can edit)
    !(params.data?.isFromReservation && params.data?.isStockManaged && params.data?.isService) &&
    params.data?.lineRole == DocLineRole.SalesPurchaseArticle;

  private isExtendedPropertiesCellEditable = (
    params: EditableCallbackParams<CreateDeliveryNoteLineDto, any>,
  ) => !params.data?.articleUsesSerial && this.isCellEditable(params);

  protected columnDefs: ColDef[] = [
    {
      showRowGroup: 'lineFromId',
      cellRenderer: GroupRowCellRendererComponent,
      suppressHeaderMenuButton: true,
      pinned: 'left',
      suppressMovable: true,
      width: 40,
      resizable: false,
      suppressAutoSize: true,
    },
    {
      field: 'displayReference',
      headerName: '#',
      type: FieldTypes.String,
      width: 70,
      suppressNavigable: true,
      suppressHeaderMenuButton: true,
      sort: 'asc',
      pinned: 'left',
      suppressMovable: true,
      suppressAutoSize: true,
    },
    {
      field: '_metaType',
      headerName: '',
      width: 120,
      suppressNavigable: true,
      suppressHeaderMenuButton: true,
      suppressAutoSize: true,
      resizable: false,
      cellRenderer: (
        params: ICellRendererParams<CreateDeliveryNoteLineDto & { bottomRow?: boolean }>,
      ) => {
        if (params.data?.bottomRow) return '';
        let stockStr = '';
        let typeStr = '';
        if (params.data?.lineFromId == null && params.data?.isStockManaged === true) {
          // Show 'is stock managed' article line if no parent, or chapter line, or in other words only on parent lines
          stockStr = `
            <span class="badge rounded-pill" style="color:var(--bs-success); background-color: rgba(var(--bs-success-rgb), 0.1);" title="${this.localizationService.instant('Product::Article:StockManaged')}">
              <i class="digi-out-stock-inventories"></i>
            </span>`;
        }
        if (params.data?.lineRole == DocLineRole.SalesPurchaseArticle) {
          // Article line (not chapter), show backorder/reservation tag instead
          let color = 'info';
          let name = 'Reservation';
          let icon = 'digi-out-edit-dialog';
          if (!params.data?.isFromReservation) {
            color = 'warning';
            name = 'Backorder';
            icon = 'digi-out-customer-order';
          }
          typeStr = `
            <span class="badge rounded-pill" style="color:var(--bs-${color}); background-color: rgba(var(--bs-${color}-rgb), 0.1);">
              <i class="${icon} me-1"></i>${this.localizationService.instant('Doc::Doc:' + name)}
            </span>`;
        }
        return stockStr + typeStr;
      },
      colSpan: params => {
        if (params.data.bottomRow) return 13;
        return 1;
      },
    },
    {
      field: 'code',
      headerName: this.localizationService.instant('Doc::CustomerReference'),
    },
    {
      field: 'description',
      headerName: this.localizationService.instant('::Description'),
    },
    {
      field: 'articleSalesUnitCode',
      headerName: this.localizationService.instant('Product::Article:ProductSaleUnit'),
    },
    {
      field: 'remainingQuantityToDeliver',
      headerName: this.localizationService.instant('Doc::DeliveryNote:QuantityToDeliver'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.quantityDecimals,
      },
      filter: false,
    },
    {
      field: 'selectedQuantityToDeliver',
      editable: params =>
        this.allowPartialDelivery() &&
        params.data.lineRole == DocLineRole.SalesPurchaseArticle &&
        params.node.isSelected() &&
        params.data.allowPartialDelivery &&
        params.data.remainingQuantityToDeliver > 0,
      headerName: this.localizationService.instant('::Qty'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.quantityDecimals,
      },
      filter: false,
    },
    this.columnDefinitionsService.getStockLocationColDef({
      isCellEditable: this.isCellEditable,
      setLineStockageLocation: (
        lineId: string,
        stockLocationId: string,
        storage: StorageLocationLookupDto,
      ) => {
        const line = this._lines.find(x => x.id == lineId);
        if (line) {
          line.stockLocationId = stockLocationId;
          line.stockLocation = storage.code;
        }
      },
      getDocument: () => null,
      withStockOnly: (item: CreateDeliveryNoteLineDto) => item.selectedQuantityToDeliver > 0,
      withQuantity: DocType.Sales,
    }),
    this.columnDefinitionsService.getArticleLotColDef({
      isCellEditable: this.isExtendedPropertiesCellEditable,
      setArticleLot: (lineId: string, articleLotId: string, articleLot: ArticleLotDto) => {
        const line = this._lines.find(x => x.id == lineId);
        if (line) {
          line.articleLotId = articleLotId;
          line.articleLot = articleLot.code;
        }
      },
      articleLotFormatter: this.articleLotFormatter.bind(this),
    }),
    this.columnDefinitionsService.getArticleRange1ColDef(),
    this.columnDefinitionsService.getArticleRangeValue1ColDef({
      isCellEditable: this.isExtendedPropertiesCellEditable,
      setArticleRangeValue1: (lineId: string, valueId: string, value: ArticleRangeValueDto) => {
        const line = this._lines.find(x => x.id == lineId);
        if (line) {
          line.articleRangeValue1Id = valueId;
          line.articleRangeValue1 = value.name.current;
        }
      },
    }),
    this.columnDefinitionsService.getArticleRange2ColDef(),
    this.columnDefinitionsService.getArticleRangeValue2ColDef({
      isCellEditable: this.isExtendedPropertiesCellEditable,
      setArticleRangeValue2: (lineId: string, valueId: string, value: ArticleRangeValueDto) => {
        const line = this._lines.find(x => x.id == lineId);
        if (line) {
          line.articleRangeValue2Id = valueId;
          line.articleRangeValue2 = value.name.current;
        }
      },
    }),
    this.columnDefinitionsService.getArticleSerialColDef({
      isCellEditable: this.isCellEditable,
      setArticleSerial: (lineId: string, articleSerialId: string, serial: ArticleSerialDto) => {
        const line = this._lines.find(x => x.id == lineId);
        if (line) {
          line.articleSerialId = articleSerialId;
          line.articleSerial = serial.serialNumber;
        }
      },
      articleSerialFormatter: this.articleSerialFormatter.bind(this),
    }),
    {
      field: 'totalWeight',
      headerName: this.localizationService.instant('::TotalWeight'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.weightDecimals,
      },
      filter: false,
    },
    {
      field: 'totalPrice',
      headerName: this.localizationService.instant('::TotalPrice'),
      type: FieldTypes.Currency,
      filter: false,
    },
  ];
  protected pinnedBottomRowData: any[] = [this.totalRow];
  protected chapterLineRole = DocLineRole.SalesPurchaseChapter;

  constructor(
    private localizationService: LocalizationService,
    private toasterService: ToasterService,
    private deliveryNoteService: CustomerDeliveryNoteService,
    private subscriptionService: SubscriptionService,
  ) {}

  ngAfterViewInit(): void {
    this.lineGrid.grid.grid.api.setGridOption('rowSelection', {
      mode: 'multiRow',
      isRowSelectable: this.isRowSelectable,
      enableSelectionWithoutKeys: true,
      checkboxes: params => {
        if (!this.allowPartialDelivery()) return false;
        if (params.data.hasAlreadyBeenDelivered) return false;
        if (
          params.data.lineRole == DocLineRole.SalesPurchaseChapter &&
          params.data.backOrders + params.data.reservation > 0
        )
          return false;
        return params.data.remainingQuantityToDeliver != 0 && params.data.cancelledQuantity == 0;
      },
      hideDisabledCheckboxes: true,
    });

    this.lineGrid.grid.grid.api.setGridOption('selectionColumnDef', {
      pinned: 'left',
      lockPinned: true,
    });

    this.lineGrid.grid.grid.api.setGridOption('onRowSelected', event => {
      if (event.data == null) return;

      let line = event.data;

      if (event.source == 'checkboxSelected' && line.lineRole == DocLineRole.SalesPurchaseChapter) {
        let selectedNodes = this.lineGrid.grid.grid.api.getSelectedNodes();

        if (selectedNodes.some(node => node.data?.lineFromId == event.data.id)) {
          this.toasterService.warn('Doc::DeliveryNote:MustDeliverChapterLineWhenDeliveryChildLine');
          event.node.setSelected(true, false);
        }
      }

      line.selectedQuantityToDeliver = event.node.isSelected()
        ? line.remainingQuantityToDeliver
        : null;
      this.onLineQuantityToDeliverChanged(line);

      if (event.node.isSelected() && event.node.parent != null) {
        event.node.parent.setSelected(true);
      }

      this.lineGrid.grid.grid.api.applyTransaction({ update: [line] });
    });

    this.lineGrid.grid.grid.api.setGridOption(
      'onFirstDataRendered',
      (params: FirstDataRenderedEvent<any>) => {
        const nodesToSelect: IRowNode[] = [];
        params.api.forEachNode(node => {
          if (node.data.remainingQuantityToDeliver != 0 && !node.data.hasAlreadyBeenDelivered) {
            nodesToSelect.push(node);
          }
        });
        params.api.setNodesSelected({ nodes: nodesToSelect, newValue: true });
      },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDataPath = (line, paths: string[] = []) => {
    if (line == null) return paths;

    if (line.lineFromId != null) {
      paths = this.getDataPath(
        this.lines.find(x => x.id == line.lineFromId),
        paths,
      );
    }
    paths.push(line.id);
    return paths;
  };

  getContextMenuItems = _ => [];

  public isRowSelectable = (event: { data: any }): boolean => {
    if (!this.allowPartialDelivery()) return false;

    if (event.data.lineFromId == null) return true;

    let parentLine = this.lines.find(parentLine => parentLine.id == event.data.lineFromId);
    return parentLine == null || parentLine.lineRole == DocLineRole.SalesPurchaseChapter;
  };

  protected onCellValueChanged = (event: CellValueChangedEvent<any, any>) => {
    if (event.column.getColId() == 'selectedQuantityToDeliver') {
      if (
        event.data.selectedQuantityToDeliver > event.data.remainingQuantityToDeliver &&
        !event.data.allowOrderedQuantityOverrun
      ) {
        this.toasterService.error('Doc::DeliveryNote:ExceededQuantityForDelivery');
        event.data.selectedQuantityToDeliver = event.data.remainingQuantityToDeliver;
      }
      if (event.data.selectedQuantityToDeliver == 0) event.node.setSelected(false);

      this.subscriptionService.addOne(this.onLineQuantityToDeliverChanged(event.data));

      this.lineGrid.grid.grid.api.applyTransaction({ update: [event.data] });
    }
  };

  private processLines(): void {
    if (!this._lines?.length) return;
    from(this._lines)
      .pipe(
        concatMap(line => this.onLineQuantityToDeliverChanged(line)),
        tap(() => this.lineGrid.grid.grid.api.autoSizeAllColumns()),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.lineGrid.grid.grid.api.autoSizeAllColumns();
  }

  private onLineQuantityToDeliverChanged(
    line: CreateDeliveryNoteLineDto & { totalWeight?: number; totalPrice?: number },
  ): Observable<any> {
    if (line.lineRole !== DocLineRole.SalesPurchaseArticle) {
      return of(null); // Skip non-applicable lines
    }

    return this.deliveryNoteService
      .getDeliveryNoteLineTotal(
        this.salesPurchaseId,
        line.sourceLineId,
        line.selectedQuantityToDeliver,
      )
      .pipe(
        tap(({ totalWeight, totalPrice }) => {
          line.totalWeight = totalWeight;
          line.totalPrice = totalPrice;

          this.lineGrid.grid.grid.api.applyTransaction({ update: [line] });
          this.computeTotalRow();
          this.computeChapterRow(line);
        }),
      );
  }

  private computeTotalRow() {
    const articleLines = this.lines.filter(
      line => line.lineRole == DocLineRole.SalesPurchaseArticle,
    );

    this.totalRow.selectedQuantityToDeliver = articleLines.reduce(
      (total, line) => (total += line.selectedQuantityToDeliver),
      0,
    );
    this.totalRow.totalWeight = articleLines.reduce(
      (total, line) => (total += line.totalWeight),
      0,
    );
    this.totalRow.totalPrice = articleLines.reduce((total, line) => (total += line.totalPrice), 0);

    let bottomRowNode = this.lineGrid.grid.grid.api.getPinnedBottomRow(0);

    this.lineGrid.grid.grid.api.redrawRows({ rowNodes: [bottomRowNode] });
  }

  private computeChapterRow(childLine: CreateDeliveryNoteLineDto) {
    if (childLine.lineFromId == null) return;
    const articleLines = this.lines.filter(
      line =>
        line.lineRole == DocLineRole.SalesPurchaseArticle &&
        line.lineFromId == childLine.lineFromId,
    );
    let chapterLine = this.lines.find(
      line => line.lineRole == DocLineRole.SalesPurchaseChapter && line.id == childLine.lineFromId,
    );

    chapterLine.selectedQuantityToDeliver = articleLines.reduce(
      (total, line) => (total += line.selectedQuantityToDeliver),
      0,
    );
    chapterLine.totalWeight = articleLines.reduce((total, line) => (total += line.totalWeight), 0);
    chapterLine.totalPrice = articleLines.reduce((total, line) => (total += line.totalPrice), 0);

    this.lineGrid.grid.grid.api.applyTransaction({ update: [chapterLine] });
  }

  protected rowClassRules = {
    'footer-row': params => params.data?.bottomRow,
    'ag-row-disabled': params => params.data?.remainingQuantityToDeliver == 0,
  };

  private articleSerialFormatter(data: any, articleSerial: any): string {
    if (data == null || articleSerial == null) return '';
    if (typeof articleSerial === 'string')
      return articleSerial != 'articleSerial' ? articleSerial : '';
    return articleSerial.serialNumber ?? '';
  }

  private articleLotFormatter(data: any, articleLot: any): string {
    if (data == null || articleLot == null) return '';
    if (typeof articleLot === 'string') return articleLot != 'articleLot' ? articleLot : '';
    return articleLot.code ?? '';
  }
}
